import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { projectsService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const STATUS_OPTIONS = ['Planning', 'In Progress', 'Testing', 'Completed'];
const HEALTH_OPTIONS = ['On Track', 'At Risk', 'Delayed'];

const STATUS_COLORS = {
  'planning': 'bg-secondary/10 text-secondary border-secondary/20',
  'in progress': 'bg-primary/10 text-primary border-primary/20',
  'testing': 'bg-accent/10 text-accent border-accent/20',
  'completed': 'bg-success/10 text-success border-success/20',
};

const HEALTH_COLORS = {
  'on track': 'bg-success/10 text-success border-success/20',
  'at risk': 'bg-warning/10 text-warning border-warning/20',
  'delayed': 'bg-error/10 text-error border-error/20',
};

const ProjectStatusModal = ({ project, onClose, onUpdated }) => {
  const toast = useToast();
  const [status, setStatus] = useState(project?.status || 'Planning');
  const [health, setHealth] = useState(project?.health || 'On Track');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  if (!project) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const healthDb = health?.toLowerCase()?.replace(' ', '-');
      await projectsService?.update(project?.id, {
        project_status: status,
        health: healthDb,
      });
      toast?.success('Project status updated successfully');
      onUpdated?.();
      onClose();
    } catch (err) {
      toast?.error('Failed to update project status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-24 py-20 border-b border-slate-100 bg-gradient-to-r from-accent/5 to-primary/5">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-primary flex items-center justify-center">
              <Icon name="FolderOpen" size={18} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Update Status</p>
              <h2 className="text-base font-bold text-slate-900 leading-tight truncate max-w-[240px]">{project?.name}</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-24 space-y-20">
          {/* Project ID + current progress */}
          <div className="flex items-center gap-12 p-14 bg-slate-50 rounded-xl">
            <Icon name="Hash" size={16} className="text-slate-400" />
            <span className="text-xs text-slate-500 font-medium">{project?.projectId}</span>
            <div className="ml-auto flex items-center gap-8">
              <span className="text-xs text-slate-500">Progress</span>
              <span className="text-sm font-bold text-slate-900">{project?.progress}%</span>
            </div>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-10">Project Status</label>
            <div className="grid grid-cols-2 gap-8">
              {STATUS_OPTIONS?.map(s => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-14 py-10 rounded-xl text-xs font-semibold border transition-all ${
                    status === s
                      ? `${STATUS_COLORS?.[s?.toLowerCase()] || 'bg-primary/10 text-primary border-primary/20'} shadow-sm`
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Health Selection */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-10">Project Health</label>
            <div className="grid grid-cols-3 gap-8">
              {HEALTH_OPTIONS?.map(h => (
                <button
                  key={h}
                  onClick={() => setHealth(h)}
                  className={`px-10 py-10 rounded-xl text-xs font-semibold border transition-all flex items-center justify-center gap-6 ${
                    health === h
                      ? `${HEALTH_COLORS?.[h?.toLowerCase()] || 'bg-primary/10 text-primary border-primary/20'} shadow-sm`
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Icon
                    name={h === 'On Track' ? 'CheckCircle2' : h === 'At Risk' ? 'AlertTriangle' : 'XCircle'}
                    size={12}
                  />
                  {h}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-8">Add a Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              rows={3}
              placeholder="Describe what changed or any blockers..."
              className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-12 px-24 py-16 border-t border-slate-100 bg-slate-50">
          <button
            onClick={onClose}
            className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-accent to-primary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8"
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
            ) : (
              <><Icon name="Save" size={16} /> Save Changes</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectStatusModal;
